import { FileType } from '../files/file-type.enum';
import { SourceEnum } from '../nodes/node-source.types';
import type { Nullable } from '../typescript/nullable';
import {
	CustomPropertyPermissions,
	type CustomPropertySelectionOption,
} from './custom-property.interface';
import { CustomPropertyIconEnum } from './custom-property-icon.enum';
import { CustomPropertyType } from './custom-property-type.enum';
import { ProcessPropertyStatusEnum } from './custom-property-value.interface';

export enum CustomPropertyKeys {
	// #region READONLY
	CERTIFIED = 'CERTIFIED',
	CLIENT_SIDE_RENDERING = 'CLIENT_SIDE_RENDERING',
	CREATED_BY = 'CREATED_BY',
	FILE_TYPE = 'FILE_TYPE',
	GOOGLE_DRIVE_GUID = 'GOOGLE_DRIVE_GUID',
	GOOGLE_DRIVE_NAME = 'GOOGLE_DRIVE_NAME',
	GOOGLE_DRIVE_URL = 'GOOGLE_DRIVE_URL',
	GOOGLE_DRIVE_VERSION = 'GOOGLE_DRIVE_VERSION',
	INTEGRATION_ID = 'INTEGRATION_ID',
	LAST_INTEGRATION_SYNC = 'LAST_INTEGRATION_SYNC',
	LAST_SEARCHED = 'LAST_SEARCHED',
	LEARNED = 'LEARNED',
	PAGE_NUMBER = 'PAGE_NUMBER',
	SIZE = 'SIZE',
	SOURCE = 'SOURCE',
	TAGS_PROCESSED = 'TAGS_PROCESSED',
	WEBPAGE_PUBLICATION_DATE = 'WEBPAGE_PUBLICATION_DATE',
	ORIGIN_URL = 'ORIGIN_URL',
	FOUND_IN_URLS = 'FOUND_IN_URLS',
	// #region SharePoint
	SHAREPOINT_ID = 'SHAREPOINT_ID',
	SHAREPOINT_ETAG = 'SHAREPOINT_ETAG', // This value represents changes in the metadata and/or the content
	SHAREPOINT_HASH = 'SHAREPOINT_HASH',
	SHAREPOINT_PATH = 'SHAREPOINT_PATH',
	SHAREPOINT_URL = 'SHAREPOINT_URL',
	SHAREPOINT_CREATION_DATE = 'SHAREPOINT_CREATION_DATE',
	// #endregion

	// #region SMART
	AUTHORS = 'AUTHORS',
	DESCRIPTION = 'DESCRIPTION',
	DURATION = 'DURATION',
	EMBEDDED_EMAILS = 'EMBEDDED_EMAILS',
	EMBEDDED_URLS = 'EMBEDDED_URLS',
	FILE_CREATION_DATE = 'FILE_CREATION_DATE',
	LANGUAGE = 'LANGUAGE',
	PHONE = 'PHONE',
	WIKIPEDIA_URL = 'WIKIPEDIA_URL',
	//#endregion
}

export interface CustomPropertyKeyToTypeMap {
	[CustomPropertyKeys.FILE_TYPE]: string;
	[CustomPropertyKeys.DESCRIPTION]: string;
	[CustomPropertyKeys.SIZE]: number;
	[CustomPropertyKeys.LAST_SEARCHED]: number;
	[CustomPropertyKeys.PAGE_NUMBER]: number;
	[CustomPropertyKeys.DURATION]: number;
	[CustomPropertyKeys.INTEGRATION_ID]: string;
	[CustomPropertyKeys.LAST_INTEGRATION_SYNC]: number;
	[CustomPropertyKeys.GOOGLE_DRIVE_GUID]: string[];
	[CustomPropertyKeys.GOOGLE_DRIVE_VERSION]: string;
	[CustomPropertyKeys.GOOGLE_DRIVE_URL]: string[];
	[CustomPropertyKeys.GOOGLE_DRIVE_NAME]: string[];
	[CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE]: number;

	//#region SharePoint
	[CustomPropertyKeys.SHAREPOINT_ID]: string[];
	[CustomPropertyKeys.SHAREPOINT_ETAG]: string;
	[CustomPropertyKeys.SHAREPOINT_HASH]: string;
	[CustomPropertyKeys.SHAREPOINT_PATH]: string[];
	[CustomPropertyKeys.SHAREPOINT_URL]: string[];
	[CustomPropertyKeys.SHAREPOINT_CREATION_DATE]: number;
	//#endregion

	[CustomPropertyKeys.FILE_CREATION_DATE]: number;
	[CustomPropertyKeys.CREATED_BY]: string[];
	[CustomPropertyKeys.LEARNED]: ProcessPropertyStatusEnum;
	[CustomPropertyKeys.TAGS_PROCESSED]: ProcessPropertyStatusEnum;

	[CustomPropertyKeys.AUTHORS]: string[];
	[CustomPropertyKeys.EMBEDDED_EMAILS]: string[];
	[CustomPropertyKeys.EMBEDDED_URLS]: string[];
	[CustomPropertyKeys.SOURCE]: string[];
	[CustomPropertyKeys.WIKIPEDIA_URL]: string;
	[CustomPropertyKeys.LANGUAGE]: string;
	[CustomPropertyKeys.CLIENT_SIDE_RENDERING]: boolean;
	[CustomPropertyKeys.ORIGIN_URL]: string;
	[CustomPropertyKeys.FOUND_IN_URLS]: string[];

	[CustomPropertyKeys.CERTIFIED]: boolean;
	[CustomPropertyKeys.PHONE]: string;
}

export type CustomPropertyKeyType<KEY extends CustomPropertyKeys> = CustomPropertyKeyToTypeMap[KEY];

export const READONLY_KEYS = [
	CustomPropertyKeys.FILE_TYPE satisfies CustomPropertyKeys.FILE_TYPE as CustomPropertyKeys.FILE_TYPE,
	CustomPropertyKeys.LAST_SEARCHED satisfies CustomPropertyKeys.LAST_SEARCHED as CustomPropertyKeys.LAST_SEARCHED,
	CustomPropertyKeys.PAGE_NUMBER satisfies CustomPropertyKeys.PAGE_NUMBER as CustomPropertyKeys.PAGE_NUMBER,
	CustomPropertyKeys.SIZE satisfies CustomPropertyKeys.SIZE as CustomPropertyKeys.SIZE,
	CustomPropertyKeys.INTEGRATION_ID satisfies CustomPropertyKeys.INTEGRATION_ID as CustomPropertyKeys.INTEGRATION_ID,
	CustomPropertyKeys.LAST_INTEGRATION_SYNC satisfies CustomPropertyKeys.LAST_INTEGRATION_SYNC as CustomPropertyKeys.LAST_INTEGRATION_SYNC,
	CustomPropertyKeys.SOURCE satisfies CustomPropertyKeys.SOURCE as CustomPropertyKeys.SOURCE,
	CustomPropertyKeys.CREATED_BY satisfies CustomPropertyKeys.CREATED_BY as CustomPropertyKeys.CREATED_BY,
	CustomPropertyKeys.LEARNED satisfies CustomPropertyKeys.LEARNED as CustomPropertyKeys.LEARNED,
	CustomPropertyKeys.TAGS_PROCESSED satisfies CustomPropertyKeys.TAGS_PROCESSED as CustomPropertyKeys.TAGS_PROCESSED,
	CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE satisfies CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE as CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE,
	//#region Google Drive
	CustomPropertyKeys.GOOGLE_DRIVE_GUID satisfies CustomPropertyKeys.GOOGLE_DRIVE_GUID as CustomPropertyKeys.GOOGLE_DRIVE_GUID,
	CustomPropertyKeys.GOOGLE_DRIVE_VERSION satisfies CustomPropertyKeys.GOOGLE_DRIVE_VERSION as CustomPropertyKeys.GOOGLE_DRIVE_VERSION,
	CustomPropertyKeys.GOOGLE_DRIVE_URL satisfies CustomPropertyKeys.GOOGLE_DRIVE_URL as CustomPropertyKeys.GOOGLE_DRIVE_URL,
	CustomPropertyKeys.GOOGLE_DRIVE_NAME satisfies CustomPropertyKeys.GOOGLE_DRIVE_NAME as CustomPropertyKeys.GOOGLE_DRIVE_NAME,
	//#region SharePoint
	CustomPropertyKeys.SHAREPOINT_ID satisfies CustomPropertyKeys.SHAREPOINT_ID as CustomPropertyKeys.SHAREPOINT_ID,
	CustomPropertyKeys.SHAREPOINT_ETAG satisfies CustomPropertyKeys.SHAREPOINT_ETAG as CustomPropertyKeys.SHAREPOINT_ETAG,
	CustomPropertyKeys.SHAREPOINT_HASH satisfies CustomPropertyKeys.SHAREPOINT_HASH as CustomPropertyKeys.SHAREPOINT_HASH,
	CustomPropertyKeys.SHAREPOINT_PATH satisfies CustomPropertyKeys.SHAREPOINT_PATH as CustomPropertyKeys.SHAREPOINT_PATH,
	CustomPropertyKeys.SHAREPOINT_URL satisfies CustomPropertyKeys.SHAREPOINT_URL as CustomPropertyKeys.SHAREPOINT_URL,
	CustomPropertyKeys.SHAREPOINT_CREATION_DATE satisfies CustomPropertyKeys.SHAREPOINT_CREATION_DATE as CustomPropertyKeys.SHAREPOINT_CREATION_DATE,
	//#endregion
	CustomPropertyKeys.CLIENT_SIDE_RENDERING satisfies CustomPropertyKeys.CLIENT_SIDE_RENDERING as CustomPropertyKeys.CLIENT_SIDE_RENDERING,
	CustomPropertyKeys.ORIGIN_URL satisfies CustomPropertyKeys.ORIGIN_URL as CustomPropertyKeys.ORIGIN_URL,
	CustomPropertyKeys.FOUND_IN_URLS satisfies CustomPropertyKeys.FOUND_IN_URLS as CustomPropertyKeys.FOUND_IN_URLS,
] as const;

export const SMART_KEYS = [
	CustomPropertyKeys.AUTHORS satisfies CustomPropertyKeys.AUTHORS as CustomPropertyKeys.AUTHORS,
	CustomPropertyKeys.CERTIFIED satisfies CustomPropertyKeys.CERTIFIED as CustomPropertyKeys.CERTIFIED,
	CustomPropertyKeys.DESCRIPTION satisfies CustomPropertyKeys.DESCRIPTION as CustomPropertyKeys.DESCRIPTION,
	CustomPropertyKeys.DURATION satisfies CustomPropertyKeys.DURATION as CustomPropertyKeys.DURATION,
	CustomPropertyKeys.EMBEDDED_EMAILS satisfies CustomPropertyKeys.EMBEDDED_EMAILS as CustomPropertyKeys.EMBEDDED_EMAILS,
	CustomPropertyKeys.EMBEDDED_URLS satisfies CustomPropertyKeys.EMBEDDED_URLS as CustomPropertyKeys.EMBEDDED_URLS,
	CustomPropertyKeys.FILE_CREATION_DATE satisfies CustomPropertyKeys.FILE_CREATION_DATE as CustomPropertyKeys.FILE_CREATION_DATE,
	CustomPropertyKeys.LANGUAGE satisfies CustomPropertyKeys.LANGUAGE as CustomPropertyKeys.LANGUAGE,
	CustomPropertyKeys.PHONE satisfies CustomPropertyKeys.PHONE as CustomPropertyKeys.PHONE,
	CustomPropertyKeys.WIKIPEDIA_URL satisfies CustomPropertyKeys.WIKIPEDIA_URL as CustomPropertyKeys.WIKIPEDIA_URL,
] as const;

export const ALL_KEYS: readonly [...typeof READONLY_KEYS, ...typeof SMART_KEYS] = [
	...READONLY_KEYS,
	...SMART_KEYS,
] as const;

export const keyToPermissionsMapper = (key: string): CustomPropertyPermissions[] => {
	if (READONLY_KEYS.includes(key as unknown as (typeof READONLY_KEYS)[number])) {
		return [];
	} else if (SMART_KEYS.includes(key as unknown as (typeof SMART_KEYS)[number])) {
		return [CustomPropertyPermissions.ADD_EDIT_REMOVE_FROM_NODE];
	} else {
		return [
			CustomPropertyPermissions.REMOVE_FROM_USER,
			CustomPropertyPermissions.ADD_EDIT_REMOVE_FROM_NODE,
		];
	}
};

export const keyToName: Record<CustomPropertyKeys, string> = {
	// READONLY
	[CustomPropertyKeys.FILE_TYPE]: 'File Type',
	[CustomPropertyKeys.LAST_SEARCHED]: 'Last Searched',
	[CustomPropertyKeys.PAGE_NUMBER]: 'Page Number',
	[CustomPropertyKeys.INTEGRATION_ID]: 'Integration ID',
	[CustomPropertyKeys.LAST_INTEGRATION_SYNC]: 'Last Synchronization',
	[CustomPropertyKeys.SOURCE]: 'Source',
	[CustomPropertyKeys.SIZE]: 'Size',
	[CustomPropertyKeys.GOOGLE_DRIVE_GUID]: 'Google Drive GUID',
	[CustomPropertyKeys.GOOGLE_DRIVE_VERSION]: 'Google Drive version',
	[CustomPropertyKeys.GOOGLE_DRIVE_URL]: 'Google Drive URL',
	[CustomPropertyKeys.GOOGLE_DRIVE_NAME]: 'Google Drive Name',
	[CustomPropertyKeys.CREATED_BY]: 'Created By',
	[CustomPropertyKeys.LEARNED]: 'AI Status',
	[CustomPropertyKeys.TAGS_PROCESSED]: 'Tags Recommended',
	[CustomPropertyKeys.CLIENT_SIDE_RENDERING]: 'Client Side Rendering',
	[CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE]: 'Webpage Publication Date',
	[CustomPropertyKeys.ORIGIN_URL]: 'Origin URL',
	[CustomPropertyKeys.FOUND_IN_URLS]: 'Found In URLs',

	//#region SharePoint
	[CustomPropertyKeys.SHAREPOINT_ID]: 'SharePoint ID',
	[CustomPropertyKeys.SHAREPOINT_ETAG]: 'SharePoint ETag',
	[CustomPropertyKeys.SHAREPOINT_HASH]: 'SharePoint Hash',
	[CustomPropertyKeys.SHAREPOINT_PATH]: 'SharePoint Path',
	[CustomPropertyKeys.SHAREPOINT_URL]: 'SharePoint URL',
	[CustomPropertyKeys.SHAREPOINT_CREATION_DATE]: 'SharePoint Creation Date',
	//#endregion

	//SMART
	[CustomPropertyKeys.DESCRIPTION]: 'Description',
	[CustomPropertyKeys.AUTHORS]: 'Authors',
	[CustomPropertyKeys.DURATION]: 'Duration',
	[CustomPropertyKeys.FILE_CREATION_DATE]: 'File Creation Date',
	[CustomPropertyKeys.EMBEDDED_EMAILS]: 'Embedded Emails',
	[CustomPropertyKeys.EMBEDDED_URLS]: 'Embedded URLS',
	[CustomPropertyKeys.WIKIPEDIA_URL]: 'Wikipedia Page',
	[CustomPropertyKeys.LANGUAGE]: 'Language',
	[CustomPropertyKeys.CERTIFIED]: 'Certified',
	[CustomPropertyKeys.PHONE]: 'Phone',
};

export const keyToType: Record<CustomPropertyKeys, CustomPropertyType> = {
	// READONLY
	[CustomPropertyKeys.CLIENT_SIDE_RENDERING]: CustomPropertyType.BOOLEAN,
	[CustomPropertyKeys.CREATED_BY]: CustomPropertyType.USER,
	[CustomPropertyKeys.FILE_TYPE]: CustomPropertyType.SINGLE_SELECT,
	[CustomPropertyKeys.FOUND_IN_URLS]: CustomPropertyType.URL,
	[CustomPropertyKeys.GOOGLE_DRIVE_GUID]: CustomPropertyType.TEXT_ARRAY,
	[CustomPropertyKeys.GOOGLE_DRIVE_NAME]: CustomPropertyType.TEXT_ARRAY,
	[CustomPropertyKeys.GOOGLE_DRIVE_URL]: CustomPropertyType.URL,
	[CustomPropertyKeys.GOOGLE_DRIVE_VERSION]: CustomPropertyType.NUMBER,
	[CustomPropertyKeys.INTEGRATION_ID]: CustomPropertyType.TEXT,
	[CustomPropertyKeys.LAST_INTEGRATION_SYNC]: CustomPropertyType.DATE,
	[CustomPropertyKeys.LAST_SEARCHED]: CustomPropertyType.DATE,
	[CustomPropertyKeys.LEARNED]: CustomPropertyType.PROCESS,
	[CustomPropertyKeys.ORIGIN_URL]: CustomPropertyType.URL,
	[CustomPropertyKeys.PAGE_NUMBER]: CustomPropertyType.NUMBER,
	[CustomPropertyKeys.SHAREPOINT_ETAG]: CustomPropertyType.TEXT,
	[CustomPropertyKeys.SHAREPOINT_HASH]: CustomPropertyType.TEXT,
	[CustomPropertyKeys.SHAREPOINT_ID]: CustomPropertyType.TEXT_ARRAY,
	[CustomPropertyKeys.SHAREPOINT_PATH]: CustomPropertyType.TEXT_ARRAY,
	[CustomPropertyKeys.SHAREPOINT_URL]: CustomPropertyType.URL,
	[CustomPropertyKeys.SHAREPOINT_CREATION_DATE]: CustomPropertyType.DATE,
	[CustomPropertyKeys.SIZE]: CustomPropertyType.NUMBER,
	[CustomPropertyKeys.SOURCE]: CustomPropertyType.MULTI_SELECT,
	[CustomPropertyKeys.TAGS_PROCESSED]: CustomPropertyType.PROCESS,
	[CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE]: CustomPropertyType.DATE,

	//SMART
	[CustomPropertyKeys.AUTHORS]: CustomPropertyType.TEXT_ARRAY,
	[CustomPropertyKeys.CERTIFIED]: CustomPropertyType.BOOLEAN,
	[CustomPropertyKeys.DESCRIPTION]: CustomPropertyType.TEXT,
	[CustomPropertyKeys.DURATION]: CustomPropertyType.TIME,
	[CustomPropertyKeys.EMBEDDED_EMAILS]: CustomPropertyType.EMAIL,
	[CustomPropertyKeys.EMBEDDED_URLS]: CustomPropertyType.URL,
	[CustomPropertyKeys.FILE_CREATION_DATE]: CustomPropertyType.DATE,
	[CustomPropertyKeys.LANGUAGE]: CustomPropertyType.TEXT,
	[CustomPropertyKeys.PHONE]: CustomPropertyType.PHONE,
	[CustomPropertyKeys.WIKIPEDIA_URL]: CustomPropertyType.URL,
} as const;

export const keyToCreationOptions: Record<
	CustomPropertyKeys,
	CustomPropertySelectionOption[] | undefined
> = {
	// READONLY
	[CustomPropertyKeys.CLIENT_SIDE_RENDERING]: undefined,
	[CustomPropertyKeys.CREATED_BY]: undefined,
	[CustomPropertyKeys.FILE_TYPE]: Object.values(FileType).map((ft) => ({ value: ft })),
	[CustomPropertyKeys.FOUND_IN_URLS]: undefined,
	[CustomPropertyKeys.GOOGLE_DRIVE_GUID]: undefined,
	[CustomPropertyKeys.GOOGLE_DRIVE_NAME]: undefined,
	[CustomPropertyKeys.GOOGLE_DRIVE_URL]: undefined,
	[CustomPropertyKeys.GOOGLE_DRIVE_VERSION]: undefined,
	[CustomPropertyKeys.INTEGRATION_ID]: undefined,
	[CustomPropertyKeys.LAST_INTEGRATION_SYNC]: undefined,
	[CustomPropertyKeys.LAST_SEARCHED]: undefined,
	[CustomPropertyKeys.LEARNED]: undefined,
	[CustomPropertyKeys.ORIGIN_URL]: undefined,
	[CustomPropertyKeys.PAGE_NUMBER]: undefined,
	[CustomPropertyKeys.SHAREPOINT_ETAG]: undefined,
	[CustomPropertyKeys.SHAREPOINT_HASH]: undefined,
	[CustomPropertyKeys.SHAREPOINT_ID]: undefined,
	[CustomPropertyKeys.SHAREPOINT_PATH]: undefined,
	[CustomPropertyKeys.SHAREPOINT_URL]: undefined,
	[CustomPropertyKeys.SHAREPOINT_CREATION_DATE]: undefined,
	[CustomPropertyKeys.SIZE]: undefined,
	[CustomPropertyKeys.SOURCE]: Object.values(SourceEnum).map((source) => ({ value: source })),
	[CustomPropertyKeys.TAGS_PROCESSED]: undefined,
	[CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE]: undefined,

	//SMART
	[CustomPropertyKeys.AUTHORS]: undefined,
	[CustomPropertyKeys.CERTIFIED]: undefined,
	[CustomPropertyKeys.DESCRIPTION]: undefined,
	[CustomPropertyKeys.DURATION]: undefined,
	[CustomPropertyKeys.EMBEDDED_EMAILS]: undefined,
	[CustomPropertyKeys.EMBEDDED_URLS]: undefined,
	[CustomPropertyKeys.FILE_CREATION_DATE]: undefined,
	[CustomPropertyKeys.LANGUAGE]: undefined,
	[CustomPropertyKeys.PHONE]: undefined,
	[CustomPropertyKeys.WIKIPEDIA_URL]: undefined,
};

export const keyToDescription: Record<CustomPropertyKeys, string> = {
	// READONLY
	[CustomPropertyKeys.CLIENT_SIDE_RENDERING]:
		'Indicates whether the webpage requires client-side rendering.',
	[CustomPropertyKeys.CREATED_BY]: 'The user who created the node.',
	[CustomPropertyKeys.FILE_TYPE]: 'The format of the file, such as PDF, DOCX, etc.',
	[CustomPropertyKeys.FOUND_IN_URLS]: 'URLs where the file was found in.',
	[CustomPropertyKeys.GOOGLE_DRIVE_GUID]: 'The unique identifier of the Google Drive file.',
	[CustomPropertyKeys.GOOGLE_DRIVE_NAME]: 'The name of the Google Drive file.',
	[CustomPropertyKeys.GOOGLE_DRIVE_URL]: 'The URL of the Google Drive file.',
	[CustomPropertyKeys.GOOGLE_DRIVE_VERSION]: 'The version of the Google Drive file.',
	[CustomPropertyKeys.INTEGRATION_ID]:
		'The identifier of the integration from which the node was imported.',
	[CustomPropertyKeys.LAST_INTEGRATION_SYNC]:
		'The date and time of the last synchronisation with the integration.',
	[CustomPropertyKeys.LAST_SEARCHED]: 'The date and time when the node was last searched.',
	[CustomPropertyKeys.LEARNED]:
		'The current learning status of the AI regarding this node. The status can be "learned" meaning the AI successfully extracted information; "learning" indicating the process is ongoing; "failed to learn" or "not learned" meaning the AI did not extract any relevant data.',
	[CustomPropertyKeys.ORIGIN_URL]: 'The original URL from which the content was sourced.',
	[CustomPropertyKeys.PAGE_NUMBER]: 'The total number of pages in the document.',
	[CustomPropertyKeys.SHAREPOINT_ETAG]:
		'The ETag of the SharePoint item representing changes in metadata or content.',
	[CustomPropertyKeys.SHAREPOINT_HASH]:
		'The hash value of the SharePoint item for integrity verification.',
	[CustomPropertyKeys.SHAREPOINT_ID]: 'The unique identifier of the SharePoint item.',
	[CustomPropertyKeys.SHAREPOINT_PATH]:
		'The path of the SharePoint item within the SharePoint site.',
	[CustomPropertyKeys.SHAREPOINT_URL]: 'The URL(s) of the SharePoint item.',
	[CustomPropertyKeys.SHAREPOINT_CREATION_DATE]:
		'The date and time when the SharePoint item was originally created.',
	[CustomPropertyKeys.SIZE]:
		'The total file size of the document in appropriate units, such as megabytes (MB).',
	[CustomPropertyKeys.SOURCE]:
		'The source from which the node originated, such as “DEFAULT_CREATION” for manual uploads or “SHAREPOINT” for nodes belonging to a SharePoint integration.',
	[CustomPropertyKeys.TAGS_PROCESSED]:
		'The processing status of tag recommendations for the node.',
	[CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE]:
		'The date and time when the webpage was originally created.',
	//SMART
	[CustomPropertyKeys.AUTHORS]:
		'The name of the individual or organisation responsible for creating the node; the author of the content.',
	[CustomPropertyKeys.CERTIFIED]: 'Indicates whether the content is certified.',
	[CustomPropertyKeys.DESCRIPTION]:
		'A brief overview or summary of the node’s content, providing context or key points about the content.',
	[CustomPropertyKeys.DURATION]:
		'The duration of the audio or video content in appropriate units, such as seconds.',
	[CustomPropertyKeys.EMBEDDED_EMAILS]: 'Email addresses embedded within the content.',
	[CustomPropertyKeys.EMBEDDED_URLS]: 'URLs embedded within the content.',
	[CustomPropertyKeys.FILE_CREATION_DATE]:
		'The date and time when the file was originally created.',
	[CustomPropertyKeys.LANGUAGE]: 'The language in which the content is written.',
	[CustomPropertyKeys.PHONE]: 'Phone number associated with the content.',
	[CustomPropertyKeys.WIKIPEDIA_URL]: 'The URL of the related Wikipedia page.',
};
export const keyToIcon: Record<CustomPropertyKeys, Nullable<CustomPropertyIconEnum>> = {
	[CustomPropertyKeys.FILE_TYPE]: null,
	[CustomPropertyKeys.LAST_SEARCHED]: null,
	[CustomPropertyKeys.PAGE_NUMBER]: null,
	[CustomPropertyKeys.INTEGRATION_ID]: null,
	[CustomPropertyKeys.LAST_INTEGRATION_SYNC]: null,
	[CustomPropertyKeys.SOURCE]: null,
	[CustomPropertyKeys.SIZE]: null,
	[CustomPropertyKeys.GOOGLE_DRIVE_GUID]: null,
	[CustomPropertyKeys.GOOGLE_DRIVE_VERSION]: null,
	[CustomPropertyKeys.GOOGLE_DRIVE_URL]: null,
	[CustomPropertyKeys.GOOGLE_DRIVE_NAME]: null,
	[CustomPropertyKeys.CREATED_BY]: null,
	[CustomPropertyKeys.LEARNED]: null,
	[CustomPropertyKeys.TAGS_PROCESSED]: null,
	[CustomPropertyKeys.CLIENT_SIDE_RENDERING]: null,
	[CustomPropertyKeys.WEBPAGE_PUBLICATION_DATE]: null,
	[CustomPropertyKeys.FOUND_IN_URLS]: null,
	[CustomPropertyKeys.ORIGIN_URL]: null,
	//#region SharePoint
	[CustomPropertyKeys.SHAREPOINT_ID]: null,
	[CustomPropertyKeys.SHAREPOINT_ETAG]: null,
	[CustomPropertyKeys.SHAREPOINT_HASH]: null,
	[CustomPropertyKeys.SHAREPOINT_PATH]: null,
	[CustomPropertyKeys.SHAREPOINT_URL]: null,
	[CustomPropertyKeys.SHAREPOINT_CREATION_DATE]: null,
	//#endregion

	//SMART
	[CustomPropertyKeys.DESCRIPTION]: null,
	[CustomPropertyKeys.AUTHORS]: null,
	[CustomPropertyKeys.DURATION]: null,
	[CustomPropertyKeys.FILE_CREATION_DATE]: null,
	[CustomPropertyKeys.EMBEDDED_EMAILS]: null,
	[CustomPropertyKeys.EMBEDDED_URLS]: null,
	[CustomPropertyKeys.WIKIPEDIA_URL]: null,
	[CustomPropertyKeys.LANGUAGE]: null,
	[CustomPropertyKeys.CERTIFIED]: null,
	[CustomPropertyKeys.PHONE]: null,
};
export const hiddenNodePropertiesIds: CustomPropertyKeys[] = [
	// CustomPropertyKeys.EMBEDDED_URLS,
	CustomPropertyKeys.EMBEDDED_EMAILS,
	CustomPropertyKeys.INTEGRATION_ID,
	// CustomPropertyKeys.LANGUAGE,
	CustomPropertyKeys.LAST_SEARCHED,
	//#region SharePoint
	CustomPropertyKeys.SHAREPOINT_ID,
	CustomPropertyKeys.SHAREPOINT_ETAG,
	CustomPropertyKeys.SHAREPOINT_HASH,
	CustomPropertyKeys.SHAREPOINT_PATH,
	//#endregion
	CustomPropertyKeys.CLIENT_SIDE_RENDERING,
	CustomPropertyKeys.ORIGIN_URL,
];

export const hiddenNodePropertiesTypes: CustomPropertyType[] = [
	// CustomPropertyType.MULTI_SELECT_SOURCE,
	CustomPropertyType.PROCESS,
	CustomPropertyType.USER,
];

export const aIBlacklistedHiddenProperties: CustomPropertyKeys[] = [
	CustomPropertyKeys.CREATED_BY,
	CustomPropertyKeys.CLIENT_SIDE_RENDERING,
	CustomPropertyKeys.FOUND_IN_URLS,
	CustomPropertyKeys.GOOGLE_DRIVE_GUID,
	CustomPropertyKeys.GOOGLE_DRIVE_NAME,
	CustomPropertyKeys.GOOGLE_DRIVE_URL,
	CustomPropertyKeys.GOOGLE_DRIVE_VERSION,
	CustomPropertyKeys.INTEGRATION_ID,
	CustomPropertyKeys.LAST_INTEGRATION_SYNC,
	CustomPropertyKeys.LAST_SEARCHED,
	CustomPropertyKeys.LEARNED,
	CustomPropertyKeys.ORIGIN_URL,
	CustomPropertyKeys.PAGE_NUMBER,
	CustomPropertyKeys.SHAREPOINT_ETAG,
	CustomPropertyKeys.SHAREPOINT_HASH,
	CustomPropertyKeys.SHAREPOINT_ID,
	CustomPropertyKeys.SHAREPOINT_PATH,
	CustomPropertyKeys.SIZE,
	CustomPropertyKeys.DURATION,
	CustomPropertyKeys.EMBEDDED_EMAILS,
	CustomPropertyKeys.EMBEDDED_URLS,
	CustomPropertyKeys.PHONE,
	CustomPropertyKeys.TAGS_PROCESSED,
	CustomPropertyKeys.WIKIPEDIA_URL,
];
